import { DeleteProceedingUseCase } from "@beneficiary/domain/usecases/linked-projects/delete-proceeding.usecase";
import { GetActivitiesByIdsUseCase } from "@beneficiary/domain/usecases/linked-projects/get-activities-by-ids.usecase";
import { Pagination } from "@core/domain/models/pagination";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import i18next from "@i18n";
import { ProceedingsSummary } from "@proceeding/domain/models/proceedings-sumary.model";
import { GetProceedingsByUseCase } from "@proceeding/domain/usecases/get-proceedings-by.usecase";
import { ActivitySummary } from "@project/domain/models/activity-summary.model";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    flow,
    flowResult,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

const MAX_ITEMS_PER_PAGE = 10;

export interface ProceedingTable {
    id: number;
    entity: string;
    entityId: number;
    project: string;
    status: string;
    modifiedDate: string;
    activities: string[];
}

export interface ProceedingsTable {
    expedients: ProceedingTable[];
    count: number;
}

@injectable()
export class ProjectsTabViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    beneficiaryId: number = 0;

    @observable
    private _proceedings: ProceedingsSummary = new ProceedingsSummary([], 0);

    @observable
    private _activitiesByIdMap = new Map<number, ActivitySummary>();

    @computed
    get proceedings(): ProceedingsTable {
        return {
            expedients:
                this._proceedings.proceedingsSummary.map<ProceedingTable>(
                    (proceedingSummary) => ({
                        id: proceedingSummary.id,
                        entity: proceedingSummary.entity.name,
                        entityId: proceedingSummary.entity.id,
                        status: proceedingSummary.statusLabel,
                        modifiedDate:
                            proceedingSummary.modifiedDate?.toISODate() ?? "",
                        project: proceedingSummary.nameProject,
                        activities: (proceedingSummary.activities || []).map(
                            (id) =>
                                this._activitiesByIdMap.get(id)?.activity ||
                                `ID: ${id}`,
                        ),
                    }),
                ),
            count: this._proceedings.count,
        };
    }

    @observable
    pagination: Pagination = new Pagination(1, MAX_ITEMS_PER_PAGE);

    constructor(
        @inject(GetProceedingsByUseCase)
        private readonly getProceedingsByUseCase: GetProceedingsByUseCase,
        @inject(GetActivitiesByIdsUseCase) // Añadir este caso de uso
        private readonly getActivitiesByIdsUseCase: GetActivitiesByIdsUseCase,
        @inject(DeleteProceedingUseCase)
        private readonly deleteProceedingUseCase: DeleteProceedingUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        this.initViewData();
    }

    @flow
    async *deleteProceeding(
        proceedingId: number,
    ): AsyncGenerator<boolean, void, unknown> {
        // Llamar al caso de uso para eliminar
        const success =
            yield this.deleteProceedingUseCase.execute(proceedingId);

        // Si se eliminó correctamente, refrescar la lista
        if (success) {
            const message = i18next.t("private:proceeding.delete.success");
            ToastManagerStore.success(message);
            yield flowResult(this.getProceedingsSummary());
        }
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        yield flowResult(this.getProceedingsSummary());

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;

        this.getProceedingsSummary();
    }

    @action
    setBeneficiaryId(beneficiaryId: number): void {
        this.beneficiaryId = beneficiaryId;
    }

    @flow
    async *getProceedingsSummary(): AsyncGenerator<
        ActivitySummary[] | void,
        void,
        unknown
    > {
        const proceedings = await this.getProceedingsByUseCase.execute(
            this.pagination,
            { beneficiaryId: this.beneficiaryId },
        );

        // Recopilar todos los IDs de actividades
        const activityIds = proceedings.proceedingsSummary
            .flatMap((p) => p.activities || [])
            .filter((id, index, self) => self.indexOf(id) === index); // Eliminar duplicados

        // Si hay actividades, cargarlas
        if (activityIds.length > 0) {
            const activities =
                yield this.getActivitiesByIdsUseCase.execute(activityIds);

            if (Array.isArray(activities)) {
                // Mapeamos directamente usando el nombre del campo que viene en la API
                this._activitiesByIdMap = new Map(
                    activities.map((activity: any) => [
                        activity.id,
                        {
                            id: activity.id,
                            activity: activity.name, // Mapear name → activity
                            description: activity.description,
                        },
                    ]),
                );
            }
        }

        runInAction(() => {
            this._proceedings = proceedings;
        });
    }

    // Public method to allow external components to trigger a refresh
    async reloadProceedings(): Promise<void> {
        await flowResult(this.getProceedingsSummary());
    }
}
